import { useEffect, useState } from 'react';
import { cantineApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ForkKnife, Warning, Money } from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';

const ParentCantine = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [studentId, setStudentId] = useState('');
  const [students, setStudents] = useState([]);
  const [menu, setMenu] = useState(null);
  const [allergies, setAllergies] = useState({ allergies: [], diet_notes: '' });
  const [allergyInput, setAllergyInput] = useState('');
  const [payments, setPayments] = useState(null);

  useEffect(() => {
    // Hydrate parent's children
    const ids = user?.student_ids || [];
    if (ids.length === 0) return;
    Promise.all(ids.map(id =>
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/students/${id}`, { withCredentials: true })
        .then(r => r.data).catch(() => null)
    )).then(arr => {
      const list = arr.filter(Boolean);
      setStudents(list);
      if (list[0]) setStudentId(list[0].id);
    });
    cantineApi.todayMenu().then(({ data }) => setMenu(data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!studentId) return;
    cantineApi.getAllergies(studentId).then(({ data }) => setAllergies({
      allergies: data.allergies || [], diet_notes: data.diet_notes || ''
    })).catch(() => {});
    cantineApi.payments(studentId).then(({ data }) => setPayments(data)).catch(() => {});
  }, [studentId]);

  const addAllergy = () => {
    const a = allergyInput.trim();
    if (!a || allergies.allergies.includes(a)) return;
    setAllergies({ ...allergies, allergies: [...allergies.allergies, a] });
    setAllergyInput('');
  };

  const removeAllergy = (a) => setAllergies({ ...allergies, allergies: allergies.allergies.filter(x => x !== a) });

  const save = async () => {
    try {
      await cantineApi.setAllergies(studentId, allergies);
      toast.success(t('saved'));
    } catch { toast.error(t('error')); }
  };

  const fmt = (n) => new Intl.NumberFormat('fr-FR').format(n || 0) + ' FCFA';

  return (
    <div className="space-y-6" data-testid="parent-cantine">
      <div className="flex items-center gap-3">
        <ForkKnife size={28} className="text-[#10B981]" weight="duotone" />
        <h1 className="font-outfit text-2xl font-bold">{t('canteen')}</h1>
      </div>

      {students.length > 1 && (
        <select className="border border-[#E2E8F0] rounded-md p-2 text-sm"
          value={studentId} onChange={e => setStudentId(e.target.value)} data-testid="parent-cantine-child-picker">
          {students.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
        </select>
      )}

      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader><CardTitle className="font-outfit text-base">{t('todayMenu')}</CardTitle></CardHeader>
        <CardContent>
          {menu ? (
            <div className="text-sm space-y-1" data-testid="parent-today-menu">
              <p>🥗 <span className="font-medium">{t('starter')}:</span> {menu.starter || '—'}</p>
              <p>🍽️ <span className="font-medium">{t('mainCourse')}:</span> {menu.main || '—'}</p>
              <p>🍰 <span className="font-medium">{t('dessert')}:</span> {menu.dessert || '—'}</p>
              {menu.notes && <p className="text-xs italic text-[#64748B] mt-2">{menu.notes}</p>}
            </div>
          ) : <p className="text-sm text-[#94A3B8]">{t('noPublishedMenu')}</p>}
        </CardContent>
      </Card>

      <Card className="border border-amber-200 shadow-none">
        <CardHeader><CardTitle className="font-outfit text-base flex items-center gap-2">
          <Warning size={18} className="text-amber-600" /> {t('allergiesAndDiet')}
        </CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>{t('declaredAllergies')}</Label>
            <div className="flex flex-wrap gap-2 mt-2 mb-2 min-h-8">
              {allergies.allergies.map(a => (
                <span key={a} className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs flex items-center gap-1">
                  {a}
                  <button onClick={() => removeAllergy(a)} className="ml-1 font-bold" data-testid={`remove-allergy-${a}`}>×</button>
                </span>
              ))}
              {allergies.allergies.length === 0 && <span className="text-xs text-[#94A3B8]">{t('noAllergies')}</span>}
            </div>
            <div className="flex gap-2">
              <Input value={allergyInput} onChange={e => setAllergyInput(e.target.value)}
                placeholder={t('allergyExample')} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addAllergy())}
                data-testid="add-allergy-input" />
              <Button onClick={addAllergy} variant="outline" data-testid="add-allergy-btn">{t('addAllergy')}</Button>
            </div>
          </div>
          <div>
            <Label>{t('dietNotes')}</Label>
            <Input value={allergies.diet_notes} onChange={e => setAllergies({ ...allergies, diet_notes: e.target.value })}
              placeholder={t('dietNotesPlaceholder')} data-testid="diet-notes-input" />
          </div>
          <Button onClick={save} data-testid="save-allergies-btn">{t('save')}</Button>
        </CardContent>
      </Card>

      {payments && (
        <Card className="border border-[#E2E8F0] shadow-none" data-testid="parent-cantine-payments">
          <CardHeader><CardTitle className="font-outfit text-base flex items-center gap-2">
            <Money size={18} className="text-[#0052FF]" /> {t('cantineFees')}
          </CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2 text-center mb-3">
              <div><p className="text-xs text-[#64748B]">{t('total')}</p><p className="font-bold">{fmt(payments.canteen_total)}</p></div>
              <div><p className="text-xs text-[#64748B]">{t('paid')}</p><p className="font-bold text-green-600">{fmt(payments.canteen_paid)}</p></div>
              <div><p className="text-xs text-[#64748B]">{t('balance')}</p><p className="font-bold text-red-600">{fmt(payments.canteen_balance)}</p></div>
            </div>
            {payments.payments.length > 0 && (
              <ul className="text-xs divide-y divide-[#F1F5F9] max-h-40 overflow-auto">
                {payments.payments.map(p => (
                  <li key={p.id} className="py-1.5 flex justify-between">
                    <span>{p.date}</span><span className="font-medium">{fmt(p.amount)}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ParentCantine;
